// ============================================================
// AuditSchedulesCard.js — read-only schedules table + Add Schedule
// modal. Add posts to /api/careaudit/schedules (existing user-tenant
// route — admin tokens are accepted by it). Toggle IsActive via
// PATCH /api/careaudit/schedules/{scheduleId}.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Modal from '../../components/Modal';
import Field from '../../components/Field';
import Switch from '../../components/Switch';
import Badge from '../../components/Badge';
import { toast } from '../../components/Toaster';
import {
  listClientAuditSchedules, createCareAuditSchedule, patchCareAuditSchedule,
} from '../../api/adminClientConfig';
import { listPlatformAuditTemplates } from '../../api/adminPlatform';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';
import { formatDate } from '../../utils/format';

const BLANK_FORM = { auditTemplateId: '', frequencyMonths: 12, nextDueDate: '' };

export default function AuditSchedulesCard({ clientId }) {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listClientAuditSchedules(clientId);
      setItems(res.items || []);
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Templates fetched once on first form open.
  async function ensureTemplates() {
    if (templates.length > 0) return;
    try {
      const res = await listPlatformAuditTemplates();
      setTemplates((res.items || []).filter((t) => t.isActive));
    } catch { /* form will show empty dropdown */ }
  }

  function openAdd() {
    setForm(BLANK_FORM); setFormError(null); setIsFormOpen(true);
    ensureTemplates();
  }
  function closeForm() {
    if (submitting) return;
    setIsFormOpen(false); setForm(BLANK_FORM);
  }

  async function submitForm(e) {
    e.preventDefault();
    setFormError(null); setSubmitting(true);
    try {
      await createCareAuditSchedule({
        clientId,
        auditTemplateId: form.auditTemplateId,
        frequencyMonths: form.frequencyMonths,
        nextDueDate: form.nextDueDate,
      });
      toast.success('Schedule created');
      setIsFormOpen(false); setForm(BLANK_FORM);
      await fetchData();
    } catch (err) { setFormError(translateError(err)); }
    finally { setSubmitting(false); }
  }

  async function toggleActive(row, nextActive) {
    setPendingId(row.auditScheduleId);
    try {
      await patchCareAuditSchedule(row.auditScheduleId, { isActive: nextActive });
      toast.success(nextActive ? 'Schedule activated' : 'Schedule deactivated');
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setPendingId(null); }
  }

  if (loading) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;
  if (error)   return <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button type="button" className="pc-btn-primary" onClick={openAdd}>
          <Plus size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Add Schedule
        </button>
      </div>
      <table className="pc-table">
        <thead>
          <tr>
            <th>Template</th>
            <th style={{ width: 90 }}>Variant</th>
            <th style={{ width: 110 }}>Frequency</th>
            <th style={{ width: 130 }}>Next Due</th>
            <th style={{ width: 80 }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: 16 }}>No schedules configured.</td></tr>
          )}
          {items.map((r) => (
            <tr key={r.auditScheduleId}>
              <td>{r.templateTitle || r.auditTemplateId}</td>
              <td>{r.templateVariant && <Badge variant="neutral">{r.templateVariant}</Badge>}</td>
              <td>Every {r.frequencyMonths} months</td>
              <td>{formatDate(r.nextDueDate) || '—'}</td>
              <td>
                <Switch
                  checked={r.isActive}
                  onChange={(next) => toggleActive(r, next)}
                  disabled={pendingId === r.auditScheduleId}
                  ariaLabel={`${r.templateTitle} active`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal isOpen={isFormOpen} onClose={closeForm} title="Add audit schedule" closeDisabled={submitting}>
        <form onSubmit={submitForm} className="pc-form">
          <Field label="Template" required>
            <select
              value={form.auditTemplateId}
              onChange={(e) => setForm({ ...form, auditTemplateId: e.target.value })}
              required
            >
              <option value="">— Pick a template —</option>
              {templates.map((t) => (
                <option key={t.auditTemplateId} value={t.auditTemplateId}>
                  {t.title} ({t.templateVariant})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Frequency (months)" required>
            <input
              type="number" min={1} max={36}
              value={form.frequencyMonths}
              onChange={(e) => setForm({ ...form, frequencyMonths: parseInt(e.target.value, 10) || 1 })}
              required
            />
          </Field>
          <Field label="Next due date" required>
            <input
              type="date"
              value={form.nextDueDate}
              onChange={(e) => setForm({ ...form, nextDueDate: e.target.value })}
              required
            />
          </Field>
          {formError && <p style={{ color: '#B00020', fontSize: 13 }} role="alert">{formError}</p>}
          <div className="pc-actions">
            <button type="button" onClick={closeForm} disabled={submitting} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '8px 16px', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" className="pc-btn-primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
